import { Accordion, keyframes, Slider, styled } from "@mui/material";
import { colors } from '../../../theme/colors';
import {
  Box,
  TextField,
  Button,
  Card,
  Tooltip,
  Autocomplete,
} from "@mui/material";

export const GlobalTalentContainer = styled(Box)(() => ({
  background: "linear-gradient(135deg, #f5f7fa 0%,rgb(226, 231, 239) 100%)",
  minHeight: "100vh",
  padding: "20px",

  "& .search_button": {
    backgroundColor: colors.primary,
    color: "#fff",
    textTransform: "capitalize",
    alignItems: "center",
    margin: "15px 0 15px 0",
    justifyContent: "center",
    borderRadius: "12px",
    padding: "12px 24px",
    fontWeight: 600,
    boxShadow: "0 4px 15px rgba(127, 29, 29, 0.3)",
    transition: "all 0.3s ease",
    "&:hover": {
      backgroundColor: colors.primaryHover,
      transform: "translateY(-2px)",
      boxShadow: "0 6px 20px rgba(127, 29, 29, 0.4)",
    },
  },

  "& .download_button": {
    backgroundColor: colors.primary,
    color: "#fff !important",
    borderRadius: "12px",
    padding: "8px 16px",
    fontWeight: 600,
    boxShadow: "0 4px 15px rgba(127, 29, 29, 0.3)",
    transition: "all 0.3s ease",
    "&:hover": {
      backgroundColor: colors.primaryHover,
      transform: "translateY(-2px)",
      boxShadow: "0 6px 20px rgba(127, 29, 29, 0.4)",
    },
    "&.Mui-disabled": {
      backgroundColor: `${colors.primaryHover} !important`,
      color: "#ffffff !important",
      opacity: 0.9,
    },
  },

  "& .btn": {
    backgroundColor: colors.primary,
    color: "#fff",
    borderRadius: "12px",
    fontWeight: 600,
    textTransform: "capitalize",
    boxShadow: "0 4px 15px rgba(127, 29, 29, 0.3)",
    "&:hover": {
      backgroundColor: colors.primaryHover,
      boxShadow: "0 6px 20px rgba(127, 29, 29, 0.4)",
    },
    "&.Mui-disabled": {
      backgroundColor: colors.buttonDisabled.background,
      color: colors.buttonDisabled.color,
      opacity: 1,
      cursor: "not-allowed",
    },
  },

  "& .custom-scrollbar": {
    marginBottom: "20px",
    maxHeight: "600px",
    overflowY: "auto",
    overflowX: "auto",
    borderRadius: "16px",
    "&::-webkit-scrollbar": {
      width: "8px",
      height: "8px",
    },
    "&::-webkit-scrollbar-track": {
      background: "#f1f1f1",
      borderRadius: "10px",
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: colors.primary,
      borderRadius: "10px",
      border: "2px solid #f1f1f1",
      "&:hover": {
        backgroundColor: colors.primaryHover,
      },
    },
  },
}));

const slideUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

export const SearchContainer = styled(Box)({
  animation: `${slideUp} 0.8s ease-out`,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  padding: "44px 36px",
  backgroundColor: "#ffffff",
  borderRadius: "28px",
  maxWidth: "1080px",
  margin: "0 auto 36px auto",
  boxShadow: "0 10px 40px rgba(0, 0, 0, 0.05)",
  border: `1px solid ${colors.primaryLightBorder}`,
  position: "relative",
  overflow: "hidden",

  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 1,
  },

  "& > *": {
    position: "relative",
    zIndex: 2,
  },
});

export const PrimaryTextContainer = styled(Box)({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
});

export const HeaderSection = styled(Box)({
  textAlign: "center",
  marginBottom: "28px",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",

  "& .main-title": {
    color: colors.primary,
    fontWeight: 800,
    fontSize: "2.2rem",
    letterSpacing: "-0.5px",
    margin: 0,

    "@media (max-width: 768px)": {
      fontSize: "1.8rem",
    },
  },

  "& .subtitle": {
    color: colors.text.secondary,
    fontSize: "1.05rem",
    fontWeight: 500,
    marginTop: "6px",
    marginBottom: "16px",
  },
});

export const SearchIconWrapper = styled(Box)({
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: "56px",
  height: "56px",
  borderRadius: "16px",
  marginBottom: "12px",
  backgroundColor: colors.primaryLight,
  border: `1px solid ${colors.primaryLightBorder}`,
  boxShadow: "0 4px 12px rgba(127, 29, 29, 0.08)",

  "& svg": {
    fontSize: "2.2rem",
    color: colors.primary,
  },
});

export const FeatureHighlight = styled(Box)({
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "8px",
  backgroundColor: colors.primaryLight,
  borderRadius: "50px",
  padding: "8px 24px",
  border: `1px solid ${colors.primaryLightBorder}`,

  "& .feature-icon": {
    color: "#f59e0b",
    fontSize: "1.1rem",
  },

  "& .feature-text": {
    color: colors.primary,
    fontWeight: 600,
    fontSize: "0.875rem",
  },
});

export const SearchInputWrapper = styled(Box)({
  display: "flex",
  gap: "12px",
  width: "100%",
  maxWidth: "920px",
  alignItems: "center",
  padding: "5px 6px 5px 18px",
  borderRadius: "50px",
  background: "#ffffff",
  border: `1.5px solid ${colors.primaryLightBorder}`,
  boxShadow: "0 4px 20px rgba(127, 29, 29, 0.05)",
  transition: "all 0.3s ease",

  "&:focus-within": {
    borderColor: colors.primary,
    boxShadow: "0 6px 24px rgba(127, 29, 29, 0.12)",
  },

  "@media (max-width: 768px)": {
    flexDirection: "column",
    gap: "16px",
    borderRadius: "20px",
    padding: "6px",
  },
});

export const CustomTextField = styled(TextField)({
  flex: 1,
  "& .MuiOutlinedInput-root": {
    backgroundColor: "#ffffff",
    borderRadius: "30px",
    height: "46px",
    border: "none",
    boxShadow: "none",
    transition: "all 0.3s ease",
    "& input::placeholder": {
      color: "#94a3b8",
      opacity: 1,
      fontSize: "0.95rem",
    },
    "& fieldset": {
      border: "none",
    },

    "&:hover": {
      backgroundColor: "#ffffff",
      boxShadow: "none",
    },

    "&.Mui-focused": {
      backgroundColor: "#ffffff",
      boxShadow: "none",
    },

    "& input": {
      padding: "8px 12px",
      fontSize: "0.95rem",
      backgroundColor: "#ffffff",
      color: "#1e293b",
      fontWeight: 500,

      "&::placeholder": {
        opacity: 1,
      },
    },
  },

  "& .search-icon": {
    color: colors.primary,
    fontSize: "1.5rem",
    marginLeft: "4px",
  },
});

export const SearchButton = styled(Button)({
  background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.secondary} 100%)`,
  color: "#ffffff",
  borderRadius: "40px",
  height: "46px",
  fontSize: "0.95rem",
  fontWeight: 700,
  textTransform: "none",
  minWidth: "160px",
  padding: "0 24px",
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  border: "none",
  boxShadow: "0 4px 14px rgba(127, 29, 29, 0.25)",
  flexShrink: 0,
  letterSpacing: "0.2px",

  "&:hover": {
    background: `linear-gradient(135deg, ${colors.secondary} 0%, ${colors.primaryHover} 100%)`,
    boxShadow: "0 6px 20px rgba(127, 29, 29, 0.35)",
    transform: "translateY(-1px)",
  },

  "&:disabled": {
    backgroundColor: `${colors.buttonDisabled.background} !important`,
    color: `${colors.buttonDisabled.color} !important`,
    transform: "none !important",
    boxShadow: "none !important",
  },

  "& svg": {
    fontSize: "1.1rem",
  },

  "@media (max-width: 768px)": {
    width: "100%",
  },
});

export const LoadingButton = styled(Button)({
  backgroundColor: "rgba(127, 29, 29, 0.1)",
  color: colors.primary,
  borderRadius: "40px",
  height: "46px",
  fontSize: "0.95rem",
  fontWeight: 600,
  textTransform: "none",
  minWidth: "170px",
  boxShadow: "none",
  border: `1px solid ${colors.primaryLightBorder}`,
  cursor: "not-allowed",

  "& span": {
    marginLeft: "8px",
  },

  "@media (max-width: 768px)": {
    width: "100%",
  },
});

export const SuggestionsContainer = styled(Box)({
  marginTop: "32px",
  textAlign: "center",
  width: "100%",
  maxWidth: "920px",
});

export const CustomAccordion = styled(Accordion)({
  marginTop: "24px",
  width: "100%",
  maxWidth: "920px",
  borderRadius: "16px !important",
  boxShadow: "none",
  border: `1px solid ${colors.primaryLightBorder}`,
  backgroundColor: colors.primaryLight,
  overflow: "hidden",

  "&:before": {
    display: "none",
  },

  "& .MuiAccordionSummary-root": {
    backgroundColor: colors.primaryLight,
    borderRadius: "16px",
    minHeight: "52px",
    padding: "0 20px",
    "&:hover": {
      backgroundColor: colors.sidebarSelected,
    },
  },
  "& .MuiAccordionSummary-content": {
    alignItems: "center",
    margin: "12px 0",
  },
  "& .MuiAccordionDetails-root": {
    padding: "16px 20px 20px",
    backgroundColor: "#ffffff",
    borderRadius: "0 0 16px 16px",
    borderTop: `1px solid ${colors.primaryLightBorder}`,
  },

  "& .expandmore-icon": {
    color: colors.primary,
  },

  "& .expandmore-heading": {
    fontWeight: 700,
    color: colors.primary,
    display: "flex",
    alignItems: "center",
    gap: "8px",
    fontSize: "0.925rem",
  },

  "& .main-accordion-container": {
    display: "flex",
    flexWrap: "wrap",
    gap: "8px",
    "& .MuiChip-root": {
      borderColor: colors.primary,
      color: colors.primary,
      fontSize: "0.875rem",
      height: "36px",
      transition: "all 0.2s ease-in-out",
      "&:hover": {
        backgroundColor: `${colors.primary} !important`,
        color: "#fff !important",
        transform: "translateY(-1px)",
        boxShadow: "0 4px 8px rgba(127, 29, 29, 0.2)",
      },
      "&:active": {
        transform: "translateY(0)",
      },
    },
  },
});

export const FormCard = styled(Card)({
  animation: `${slideUp} 0.8s ease-out`,
  backgroundColor: "#ffffff",
  borderRadius: "28px",
  boxShadow: "0 10px 40px rgba(0, 0, 0, 0.05)",
  border: `1px solid ${colors.border.light}`,
  maxWidth: "1080px",
  margin: "36px auto",
  overflow: "visible",
  padding: "44px 36px",

  "& .form-title": {
    color: colors.primary,
    fontWeight: 800,
    fontSize: "1.8rem",
    marginBottom: "6px",
    letterSpacing: "-0.5px",
  },

  "& .form-subtitle": {
    color: colors.text.secondary,
    marginBottom: "32px",
    fontSize: "1rem",
    fontWeight: 500,
  },
  "& .advanced-filter-container": {
    padding: 0,
  },
});

export const FormSection = styled(Box)({
  "& .field-label": {
    color: colors.primary,
    fontWeight: 600,
    marginBottom: "8px",
    fontSize: "1.1rem",
  },
});

export const ExperienceSlider = styled(Slider)({
  color: colors.primary,
  height: 6,
  padding: "15px 0",

  "& .MuiSlider-track": {
    border: "none",
    backgroundColor: colors.primary,
    height: 6,
  },

  "& .MuiSlider-rail": {
    opacity: 0.3,
    backgroundColor: colors.primary,
    height: 6,
  },

  "& .MuiSlider-thumb": {
    height: 22,
    width: 22,
    backgroundColor: "#fff",
    border: `3px solid ${colors.primary}`,
    boxShadow: "0 2px 8px rgba(127, 29, 29, 0.3)",

    "&:focus, &:hover, &.Mui-active, &.Mui-focusVisible": {
      boxShadow: "0 3px 12px rgba(127, 29, 29, 0.4)",
    },

    "&::before": {
      display: "none",
    },
  },

  "& .MuiSlider-markLabel": {
    fontSize: "0.85rem",
    fontWeight: 600,
    color: "#666",
    top: 40,

    "&.MuiSlider-markLabelActive": {
      color: colors.primary,
    },
  },

  "& .MuiSlider-mark": {
    display: "none",
  },

  "& .MuiSlider-valueLabel": {
    backgroundColor: colors.primary,
    borderRadius: "8px",
    fontSize: "0.8rem",
    fontWeight: 600,
    padding: "4px 8px",
  },
});

export const LocationPickerWrapper = styled(Box)({
  position: "relative",
  width: "100%",
});

export const LocationInputField = styled(Box)({
  display: "flex",
  alignItems: "center",
  padding: "0 14px",
  borderRadius: "10px",
  border: `1px solid ${colors.primary}`,
  backgroundColor: "#fff",
  cursor: "pointer",
  transition: "all 0.3s ease",
  height: "56px",
  boxSizing: "border-box",
  position: "relative",
  overflow: "hidden",

  "& .map-bg": {
    position: "absolute",
    right: 0,
    top: 0,
    width: "250px",
    height: "100%",
    objectFit: "cover",
    opacity: 0.5,
    pointerEvents: "none",
    maskImage: "linear-gradient(to right, transparent, black 10%)",
    WebkitMaskImage: "linear-gradient(to right, transparent, black 10%)",
  },

  "&:hover": {
    borderColor: colors.primary,
  },

  "&.active": {
    borderColor: colors.primary,
    borderWidth: "2px",
    padding: "0 13px",
  },

  "& .location-icon": {
    color: colors.primary,
    fontSize: "1.3rem",
    marginRight: "10px",
    flexShrink: 0,
    zIndex: 1,
  },

  "& .location-display": {
    flex: 1,
    fontSize: "1rem",
    color: "#333",
    zIndex: 1,
  },

  "& .location-placeholder": {
    flex: 1,
    fontSize: "1rem",
    color: "rgba(0, 0, 0, 0.6)",
    zIndex: 1,
  },

  "& .dropdown-icon": {
    color: colors.primary,
    transition: "transform 0.3s ease",
    zIndex: 1,
  },

  "& .dropdown-icon.open": {
    transform: "rotate(180deg)",
  },
});

export const LocationDropdown = styled(Box)({
  position: "absolute",
  top: "calc(100% + 8px)",
  left: 0,
  right: 0,
  backgroundColor: "#fff",
  borderRadius: "16px",
  border: "1px solid #e0e0e0",
  boxShadow: "0 8px 30px rgba(0, 0, 0, 0.15)",
  zIndex: 100,
  overflow: "hidden",
  display: "flex",
  flexDirection: "row",
  maxHeight: "220px",

  "& .country-section": {
    display: "flex",
    flexDirection: "column",
    gap: "6px",
    padding: "10px",
    borderRight: "1px solid #f0f0f0",
    minWidth: "180px",
    width: "180px",
    flexShrink: 0,
    overflowY: "auto",
    overflowX: "hidden",
    scrollbarWidth: "none",
    msOverflowStyle: "none",
    "&::-webkit-scrollbar": {
      display: "none",
    },
  },

  "& .country-option": {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    padding: "10px 14px",
    cursor: "pointer",
    borderRadius: "12px",
    transition: "all 0.2s ease",
    fontSize: "0.95rem",
    fontWeight: 500,
    color: "#333",
    border: "1px solid #e0e0e0",
    whiteSpace: "nowrap",

    "&:hover": {
      backgroundColor: "#f0f4f8",
      borderColor: colors.primary,
    },

    "&.selected": {
      backgroundColor: colors.primary,
      color: "#fff",
      borderColor: colors.primary,
    },

    "& .country-flag": {
      width: "24px",
      height: "16px",
      objectFit: "cover",
      borderRadius: "2px",
      flexShrink: 0,
    },
  },

  "& .city-panel": {
    display: "flex",
    flexDirection: "column",
    flex: 1,
    minWidth: 0,
    overflow: "hidden",
  },

  "& .city-search-wrapper": {
    padding: "10px 12px",
    borderBottom: "1px solid #f0f0f0",
  },

  "& .city-search-input": {
    width: "100%",
    padding: "10px 14px",
    borderRadius: "10px",
    border: "1px solid #e0e0e0",
    fontSize: "0.9rem",
    outline: "none",
    transition: "border-color 0.2s ease",
    fontFamily: "inherit",
    boxSizing: "border-box",

    "&:focus": {
      borderColor: colors.primary,
    },

    "&::placeholder": {
      color: "#aaa",
    },
  },

  "& .city-list": {
    flex: 1,
    overflow: "hidden",

    "& > div::-webkit-scrollbar": {
      width: "6px",
    },
    "& > div::-webkit-scrollbar-track": {
      background: "#f5f5f5",
      borderRadius: "3px",
    },
    "& > div::-webkit-scrollbar-thumb": {
      backgroundColor: colors.primary,
      borderRadius: "3px",
      "&:hover": {
        backgroundColor: colors.primaryHover,
      },
    },
  },

  "& .city-option": {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    padding: "10px 14px",
    cursor: "pointer",
    borderRadius: "10px",
    transition: "all 0.2s ease",
    fontSize: "0.9rem",
    color: "#444",

    "&:hover": {
      backgroundColor: "#f0f4f8",
      color: colors.primary,
    },

    "& .city-code": {
      fontSize: "0.8rem",
      fontWeight: 600,
      color: "#999",
      flexShrink: 0,
    },
  },

  "& .loading-container": {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "24px",
    gap: "12px",
    color: "#666",
    fontSize: "0.9rem",
    flex: 1,
  },

  "& .no-results": {
    textAlign: "center",
    padding: "20px",
    color: "#999",
    fontSize: "0.9rem",
    flex: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },

  "& .select-country-hint": {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flex: 1,
    color: "#aaa",
    fontSize: "0.9rem",
    padding: "20px",
  },
});

export const CustomAutocomplete = styled(Autocomplete)({
  "& .MuiOutlinedInput-root": {
    backgroundColor: "#fff",
    borderRadius: "16px",
    boxShadow: "0 4px 15px rgba(0, 0, 0, 0.08)",
    transition: "all 0.3s ease",

    "& fieldset": {
      borderColor: colors.primary,
    },
  },

  "&.Mui-focused": {
    transform: "translateY(-2px)",
    boxShadow: "0 8px 25px rgba(0, 0, 0, 0.12)",

    "& fieldset": {
      borderColor: colors.primary,
      borderWidth: "2px",
    },
  },

  "& label.Mui-focused": {
    color: colors.primary,
    fontWeight: 600,
  },

  "& .MuiChip-root": {
    backgroundColor: colors.primary,
    color: "#fff",
    fontWeight: 500,
    borderRadius: "12px",
    margin: "2px",

    "& .MuiChip-deleteIcon": {
      color: "rgba(255, 255, 255, 0.8)",

      "&:hover": {
        color: "#fff",
      },
    },
  },
});

export const ChipContainer = styled(Box)({
  display: "flex",
  flexWrap: "wrap",
  gap: "8px",

  "& .location-chip": {
    backgroundColor: "#e3f2fd",
    color: colors.secondary,
    border: `1px solid ${colors.secondary}`,

    "& .MuiChip-deleteIcon": {
      color: colors.secondary,
    },
  },

  "& .skill-chip": {
    backgroundColor: "#f3e5f5",
    color: "#7b1fa2",
    border: "1px solid #7b1fa2",

    "& .MuiChip-deleteIcon": {
      color: "#7b1fa2",
    },
  },
});

export const SkillsTooltip = styled(Tooltip)({
  "& .MuiTooltip-tooltip": {
    backgroundColor: colors.primary,
    color: "#fff",
    fontSize: "0.9rem",
    maxWidth: "400px",
    padding: "16px",
    borderRadius: "12px",
    boxShadow: "0 8px 25px rgba(0, 0, 0, 0.3)",
  },

  "& .MuiTooltip-arrow": {
    color: colors.primary,
  },
});

export const SearchButtonContainer = styled(Box)({
  display: "flex",
  justifyContent: "center",
  marginTop: "32px",
  paddingTop: "24px",
  borderTop: "1px solid #e0e0e0",
});

export const StyledSearchButton = styled(Button)({
  background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.secondary} 100%)`,
  color: "#fff",
  borderRadius: "40px",
  fontSize: "1.05rem",
  fontWeight: 700,
  textTransform: "none",
  minWidth: "220px",
  height: "48px",
  boxShadow: "0 4px 16px rgba(127, 29, 29, 0.25)",
  transition: "all 0.3s ease",

  "&:hover": {
    background: `linear-gradient(135deg, ${colors.secondary} 0%, ${colors.primaryHover} 100%)`,
    transform: "translateY(-2px)",
    boxShadow: "0 6px 20px rgba(127, 29, 29, 0.35)",
  },

  "&.Mui-disabled": {
    backgroundColor: `${colors.buttonDisabled.background} !important`,
    color: `${colors.buttonDisabled.color} !important`,
    transform: "none !important",
    boxShadow: "none !important",
    opacity: 1,
    cursor: "not-allowed",
  },
});

export const LoadingSearchButton = styled(Button)({
  borderRadius: "16px",
  fontSize: "1.2rem",
  fontWeight: 700,
  textTransform: "none",
  minWidth: "250px",
  cursor: "not-allowed",
  backgroundColor: "rgba(0, 0, 0, 0.12)",
  color: "rgba(0, 0, 0, 0.26)",

  "& span": {
    marginLeft: "12px",
  },
});

export const ResultsSection = styled(Box)({
  marginTop: "32px",

  "& .results-grid": {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(380px, 1fr))",
    gridAutoRows: "stretch",
    gap: "20px",
    marginTop: "20px",
    alignItems: "stretch",

    "@media (max-width: 768px)": {
      gridTemplateColumns: "1fr",
    },
  },
});

export const StatsCard = styled(Card)({
  backgroundColor: "#fff",
  borderRadius: "16px",
  padding: "24px",
  boxShadow: "0 8px 25px rgba(0, 0, 0, 0.1)",
  border: "1px solid #e0e0e0",

  "& .stats-title": {
    color: colors.primary,
    fontWeight: 700,
    marginBottom: "8px",
  },

  "& .stats-count": {
    color: "#666",
    fontSize: "1.1rem",
  },
});

export const ResultCard = styled(Card)({
  backgroundColor: "#fff",
  borderRadius: "20px",
  boxShadow: "0 8px 25px rgba(0, 0, 0, 0.08)",
  border: "1px solid #e2e8f0",
  position: "relative",
  display: "flex",
  flexDirection: "column",
  height: "100%",

  transition: "box-shadow 0.3s cubic-bezier(0.4, 0, 0.2, 1), transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)",

  "&:hover": {
    boxShadow: "0 15px 35px rgba(0, 0, 0, 0.12)",
    transform: "translateY(-4px)",
  },
});

export const MeasuringOverlay = styled(Box)({
  position: "absolute",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: "rgba(255, 255, 255, 0.8)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 15,
  backdropFilter: "blur(2px)",

  "& .loading-text": {
    color: colors.primary,
    fontSize: "0.875rem",
    fontWeight: 500,
  },
});

export const ReadMoreButton = styled(Button)({
  position: "absolute",
  bottom: "16px",
  left: "50%",
  transform: "translateX(-50%)",
  backgroundColor: "rgba(255, 255, 255, 0.95)",
  color: colors.primary,
  border: `2px solid ${colors.primary}`,
  borderRadius: "20px",
  padding: "8px 24px",
  fontWeight: 600,
  fontSize: "0.875rem",
  textTransform: "none",
  boxShadow: "0 4px 15px rgba(127, 29, 29, 0.2)",
  backdropFilter: "blur(10px)",
  zIndex: 10,
  transition: "all 0.3s ease",
  minWidth: "120px",

  "&:hover": {
    backgroundColor: colors.primaryHover,
    color: "#ffffff",
    borderColor: colors.primaryHover,
    transform: "translateX(-50%) translateY(-2px)",
    boxShadow: "0 6px 20px rgba(127, 29, 29, 0.3)",
  },

  "&:active": {
    transform: "translateX(-50%) translateY(0)",
  },

  "&.measuring": {
    opacity: 0.8,
  },
});

export const GradientOverlay = styled(Box)({
  position: "absolute",
  bottom: 0,
  left: 0,
  right: 0,

  height: "120px",
  background:
    "linear-gradient(transparent, rgba(255, 255, 255, 0.8), rgba(255, 255, 255, 0.95), #ffffff)",
  pointerEvents: "none",
  zIndex: 5,
  transition: "opacity 0.3s ease",
});

export const CandidateHeader = styled(Box)({
  display: "flex",
  alignItems: "flex-start",
  gap: "16px",
  marginBottom: "8px",

  "& .avatar": {
    width: 60,
    height: 60,
    border: "2px solid #fff",
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
  },
  "& .download-icon": {
    color: colors.primary,
  },

  "& .download-icon-btn": {
    border: "1px solid #dde7f0",
    borderRadius: "10px",
    padding: "8px",
    color: colors.primary,
    backgroundColor: "#fff",
    transition: "all 0.2s ease",
    "&:hover": {
      borderColor: colors.primary,
      backgroundColor: "rgba(127, 29, 29, 0.05)",
      color: colors.primary,
    },
  },

  "& .match-score": {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end",
    gap: "6px",
    marginLeft: "auto",

    "& .score-badge": {
      backgroundColor: "#e6f4ea",
      color: "#1e7e34",
      padding: "6px 12px",
      borderRadius: "30px",
      border: "1px solid #a3d9a5",
      fontWeight: 700,
      fontSize: "0.9rem",
      lineHeight: 1,
      textAlign: "center",
      display: "inline-flex",
      alignItems: "center",
    },

    "& .score-label": {
      color: "#637381",
      fontWeight: 600,
      fontSize: "11px",
      textTransform: "uppercase",
      letterSpacing: "0.5px",
    },
  },
});

export const PersonalInfoContainer = styled(Box)({
  mt: 2,
  display: "flex",
  flexDirection: "column",
  alignItems: "start",

  flexWrap: "wrap",
  gap: 1,
  "@media (max-width: 600px)": {
    flexDirection: "column",
    gap: 0.5,
  },

  "& .candidate-location": {
    display: "flex",
    alignItems: "center",
    gap: 0.5,
  },
});

export const CandidateInfo = styled(Box)({
  flex: 1,

  "& .candidate-name": {
    color: colors.primary,
    fontWeight: 700,
    marginBottom: "4px",
  },

  "& .candidate-title": {
    color: "#666",
    fontWeight: 500,
    marginBottom: "8px",
  },
});

export const CandidateDetails = styled(Box)({
  display: "flex",
  flexWrap: "wrap",
  gap: "16px",
  marginBottom: "16px",

  "& .detail-item": {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    color: "#666",

    "& svg": {
      color: colors.primary,
    },
  },
});

export const SkillsSection = styled(Box)({
  marginBottom: "16px",

  "& .skills-container": {
    display: "flex",
    flexWrap: "wrap",
    gap: "8px",
  },

  "& .skill-chip": {
    backgroundColor: "#f4f6f8",
    color: "#454f5b",
    border: "1px solid #dfe3e8",
    fontSize: "0.8rem",
    fontWeight: 500,
    borderRadius: "6px",
    transition: "all 0.2s ease",
    "&:hover": {
      backgroundColor: "#dfe3e8",
      color: "#212b36",
    },
  },

  "& .more-chip": {
    backgroundColor: colors.primary,
    color: "#fff",
    fontSize: "0.8rem",
  },

  "& .has-more-skills": {
    backgroundColor: colors.primary,
    color: "#ffffff",
    cursor: "pointer",
    "&:hover": {
      backgroundColor: colors.primaryHover,
    },
    "&:active": {
      backgroundColor: colors.primary,
    },
  },
});

export const ExperienceSection = styled(Box)({
  marginBottom: "20px",

  "& .experience-item": {
    color: "#666",
    marginBottom: "4px",
    lineHeight: 1.4,
  },
});

export const ActionSection = styled(Box)({
  paddingTop: "16px",
  borderTop: "1px solid #e0e0e0",
  marginBottom: "60px",
});

export const StyledDownloadButton = styled(Button)({
  backgroundColor: colors.primary,
  color: "#fff",
  borderRadius: "12px",
  padding: "12px 24px",
  textTransform: "none",
  boxShadow: "0 4px 15px rgba(127, 29, 29, 0.3)",
  transition: "all 0.3s ease",

  "&:hover": {
    backgroundColor: colors.primaryHover,
    transform: "translateY(-2px)",
    boxShadow: "0 6px 20px rgba(127, 29, 29, 0.4)",
  },

  "& .download-resume": {
    color: "#fff !important",
  },
});

export const PaginationContainer = styled(Box)({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginTop: "40px",
  padding: "12px",
  backgroundColor: "#fff",
  borderRadius: "16px",
  boxShadow: "0 4px 15px rgba(0, 0, 0, 0.1)",

  "& .page-info": {
    color: "#666",
    fontWeight: 500,
    fontSize: "1rem",
  },

  "@media (max-width: 768px)": {
    flexDirection: "column",
    gap: "16px",

    "& .page-info": {
      order: -1,
    },
  },
});

export const NoResultsContainer = styled(Box)({
  textAlign: "center",
  padding: "80px 20px",
  backgroundColor: "#fff",
  borderRadius: "20px",
  boxShadow: "0 8px 25px rgba(0, 0, 0, 0.1)",
  margin: "40px 0",

  "& .no-results-icon": {
    fontSize: "4rem",
    color: "#ccc",
    marginBottom: "24px",
  },

  "& .no-results-title": {
    color: colors.primary,
    fontWeight: 700,
    marginBottom: "12px",
  },

  "& .no-results-subtitle": {
    color: "#666",
    fontSize: "1.1rem",
    maxWidth: "500px",
    margin: "0 auto",
  },
});

export const TableSection = styled(Box)({
  backgroundColor: "#fff",
  borderRadius: "20px",
  overflow: "hidden",
  boxShadow: "0 10px 30px rgba(0, 0, 0, 0.1)",
  margin: "24px 0",

  "& .table-header": {
    backgroundColor: colors.primary,
    color: "#fff !important",
    fontSize: "1rem",
    fontWeight: 600,
    padding: "16px",
    borderBottom: "none",
  },

  "& .table-row": {
    "&:hover": {
      backgroundColor: "#f8f9fa",
    },

    "& td": {
      padding: "16px",
      borderBottom: "1px solid #e0e0e0",
    },
  },
});

export const StyledFormControl = styled(Box)({
  width: "100%",
  marginBottom: "24px",
});

export const StyledInputLabel = styled(Box)({
  color: colors.primary,
  fontWeight: 600,
  marginBottom: "8px",
  fontSize: "1rem",
});

export const StyledSelect = styled(Box)({
  "& .MuiOutlinedInput-root": {
    borderRadius: "12px",
    backgroundColor: "#fff",

    "& fieldset": {
      borderColor: "#e0e0e0",
    },

    "&:hover fieldset": {
      borderColor: colors.primary,
    },

    "&.Mui-focused fieldset": {
      borderColor: colors.primary,
    },
  },
});

export const StyledMenuItem = styled(Box)({
  "&:hover": {
    backgroundColor: `${colors.primary} !important`,
    color: "#fff",
  },
});
